import { Inject, Injectable, Injector } from '@angular/core';
import { find, isString } from 'lodash';

import { IDataSourceOptions, ENTITY_TYPES, IEntityConfig } from '@shared/types';
import { BaseService, IBaseServiceClass } from '../base';
import { DataSource } from './entity-data-source.service';
import { SelectOptionsDataSource } from './select-options-datasource';
import { InfiniteScrollDataSource } from './infinite-scroll-datasource';

@Injectable()
export class EntityServiceProvider {
  public service: BaseService;
  public fallbackServiceClass: any = this.getServiceClass('basic-entity');

  public constructor(
    public injector: Injector,
    @Inject('EntityServices') public entityServices,
  ) {}

  public getServiceClass(predicate: any): IBaseServiceClass {
    const findCriteria = isString(predicate)
      ? { ENTITY_TYPE: predicate }
      : predicate;

    return find(this.entityServices, findCriteria);
  }

  public getService<R extends BaseService = BaseService>(
    predicate: any,
    notFoundValue?,
  ): R {
    const serviceClass =
      this.getServiceClass(predicate) || this.fallbackServiceClass;
    const service = this.injector.get(serviceClass, notFoundValue);

    // use and configure fallback service
    if (serviceClass === this.fallbackServiceClass && isString(predicate)) {
      const entityConfig: IEntityConfig = {
        ENTITY_TYPE: predicate as ENTITY_TYPES,
        isMasterListAvailable: null,
        buttons: null,
        apiEndpoint: predicate,
        uiRoute: predicate,
      };
      service.entityType = entityConfig.ENTITY_TYPE;
      service.config = entityConfig;
    }

    return service;
  }

  public createDataSource<R extends BaseService = BaseService>(
    entityType: string,
    options: IDataSourceOptions = {},
  ): DataSource<R> {
    if (!entityType) return null;
    const service = this.getService<R>(entityType);

    return service ? new DataSource<R>(service, options) : null;
  }

  public createSelectOptionsDataSource(
    input,
    options = {},
  ): SelectOptionsDataSource {
    return new SelectOptionsDataSource(input, options);
  }

  public createInfiniteScrollDataSource<R extends BaseService = BaseService>(
    entityType: ENTITY_TYPES,
    options: IDataSourceOptions = {},
  ): InfiniteScrollDataSource<R> {
    if (!entityType) {
      return null;
    }

    const service = this.getService<R>(entityType);

    return service ? new InfiniteScrollDataSource<R>(service, options) : null;
  }
}
